import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Combobox } from '@/shared/components/ui/combobox';
import { DatePicker } from '@/shared/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { cajaBase, esCajaEfectivoConPar } from '@/shared/lib/cajasEfectivo';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { MedioDePagoItem } from '@/shared/api/controllers/mediosDePago';
import type { ComprobantePagoItem } from '@/shared/api/controllers/comprobantePagos';
import type { Moneda } from './MoneyCell';
import { MoneyInput } from './MoneyInput';
import { FiscalDataMissingWarning } from './FiscalDataMissingWarning';

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Edit a `Pendiente de Cobro` promesa OR a `Pendiente de Confirmación`
 * pago. Mirrors PowerApps's `btn_ConfirmEditPago_P`
 * (Screen_Pagos.pa.yaml). The legacy app showed two visually distinct
 * popups (`PopUp_EditarPago` for promesas, `PopUp_EditarPagoPendConfirmacion`
 * for confirmación-pendientes); we share one component and switch
 * which fields are editable via `mode`:
 *
 *   - mode='promesa' (default): user can change MedioPago_CP, Monto_CP,
 *     FechaCobro_CP, and (USD only) TipoCambio_CP/Pesos_CP.
 *   - mode='confirmacion': MedioPago_CP is locked (Combobox shown as
 *     read-only label) — once the customer pays via a specific method
 *     the medio is fixed, only amounts/dates can be amended.
 *
 * Validation tighter than legacy: TC + Pesos only surface when the
 * original promesa carried them (USD pago) so we don't create orphan
 * USD-tracked rows on ARS-only promesas. The server-side guard rejects
 * edits on Confirmado / Anulado regardless of mode.
 */
export type EditarPagoMode = 'promesa' | 'confirmacion';

export type EditarPagoFormValue = {
  medioPagoId: string;
  medioPago: string;
  /** dd/mm/yyyy */
  fechaCobro: string;
  monto: number;
  tipoCambio?: number;
  pesos?: number;
};

export function EditarPagoDialog({
  open,
  comp,
  mediosDePago,
  mode = 'promesa',
  loading = false,
  ventaMoneda,
  disponibleAFacturar,
  disponibleSinFactura,
  isExento = false,
  clienteFiscalMissing = null,
  onSubmit,
  onCancel,
}: {
  open: boolean;
  comp: ComprobantePagoItem;
  mediosDePago: MedioDePagoItem[];
  mode?: EditarPagoMode;
  loading?: boolean;
  /** Venta currency — needed to compute the lleva-factura cap when
   *  the medio is in a different currency than the venta. */
  ventaMoneda: Moneda;
  /** Remaining factura budget across the venta, EXCLUDING the pago
   *  being edited (the parent recalculates it that way so we can
   *  treat the edit as a swap). */
  disponibleAFacturar: number;
  /** Cupo sin factura de la venta EXCLUYENDO este pago, igual que el techo.
   *  Acá el flag no se puede cambiar, así que sólo topea el monto de un pago
   *  que ya va sin factura. */
  disponibleSinFactura: number;
  /** Client is IVA Exento — the cap equals the venta total, so an over-limit
   *  means "exceeds the venta total"; show the generic "Monto excedido". */
  isExento?: boolean;
  /** AR only: receptor fiscal fields (condición IVA / CUIT) the vendedor still
   *  has to complete before this client can be facturado. Non-null → a warning
   *  shows when the edited pago lleva factura. */
  clienteFiscalMissing?: string[] | null;
  onSubmit: (value: EditarPagoFormValue) => void;
  onCancel: () => void;
}) {
  const f = comp.fields;
  // The promesa was created in USD when Pesos_CP is non-empty (it
  // stores the venta-currency equivalent). PowerApps uses this same
  // shape check to decide whether to surface TC + Pesos.
  const hasPesos = useMemo(
    () => (f.Pesos_CP ?? '').trim() !== '',
    [f.Pesos_CP],
  );

  // "Efectivo Pesos N" se muestra como su base (la gemela no está en el
  // selector); el server la vuelve a resolver con Lleva Factura al guardar.
  const initialMedio =
    mediosDePago.find((m) => m.fields.Medio_MDP === cajaBase(f.MedioPago_CP ?? '')) ?? null;

  // Local medios are denominated in the active country's home currency
  // (ARS / MXN). Drives the MoneyInput's currency formatting below.
  const { config: activeCountry } = useActiveCountry();
  const homeCurrency = activeCountry.currency.primary;
  const [medio, setMedio] = useState<MedioDePagoItem | null>(initialMedio);
  const [fechaCobro, setFechaCobro] = useState(arToIso(f.FechaCobro_CP) || todayIso());
  const [monto, setMonto] = useState(String(f.Monto_CP ?? ''));
  const [tipoCambio, setTipoCambio] = useState(String(f.TipoCambio_CP ?? ''));
  const [pesos, setPesos] = useState(String(f.Pesos_CP ?? ''));

  useEffect(() => {
    if (!open) return;
    setMedio(
      mediosDePago.find((m) => m.fields.Medio_MDP === cajaBase(f.MedioPago_CP ?? '')) ?? null,
    );
    setFechaCobro(arToIso(f.FechaCobro_CP) || todayIso());
    setMonto(String(f.Monto_CP ?? ''));
    setTipoCambio(String(f.TipoCambio_CP ?? ''));
    setPesos(String(f.Pesos_CP ?? ''));
  }, [
    open,
    comp.id,
    mediosDePago,
    f.MedioPago_CP,
    f.FechaCobro_CP,
    f.Monto_CP,
    f.TipoCambio_CP,
    f.Pesos_CP,
  ]);

  const montoNum = parseMoney(monto) ?? NaN;
  const tcNum = parseMoney(tipoCambio) ?? NaN;
  const pesosNum = parseMoney(pesos) ?? NaN;

  // confirmacion mode locks the medio, so a missing/deactivated medio
  // entry shouldn't block the user from amending monto/dates. We still
  // need *something* to send for the schema, but the server discards
  // medioPago for that status anyway.
  const medioRequired = mode === 'promesa';

  // Lleva-Factura cap: mirrors the RegistrarPagoDialog guard. The
  // pago being edited carries `ContieneFactura_CP` (or its medio is
  // a Banco that forces it). When that's true the new monto, in
  // venta currency, must fit within `disponibleAFacturar` — which
  // the parent computes EXCLUDING this pago, so the original amount
  // is "freed" and we don't double-count it.
  // En el par de efectivo "Lleva factura" es una etiqueta de la caja, no un
  // candado: ahí manda el flag del cobro.
  const medioFuerzaFactura =
    medio?.fields.LlevaFactura_MDP === 'SI' &&
    !esCajaEfectivoConPar(medio.fields.Medio_MDP ?? '');
  const contieneFactura =
    medioFuerzaFactura || (comp.fields.ContieneFactura_CP ?? '') === 'SI';
  const isInternationalMedio =
    medio?.fields.Categoria_MDP === 'Internacional' ||
    // Fall back to the saved Moneda_CP when the medio entry is missing
    // (deactivated medios on legacy rows). Pesos_CP being non-empty
    // is also a strong signal the pago was in USD.
    hasPesos;
  const needsTC = isInternationalMedio && ventaMoneda !== 'USD';
  // Value of the in-progress monto expressed in venta currency. For
  // same-currency edits it's just `montoNum`; for cross-currency
  // (USD pago on ARS venta) we use the equivalente field the user
  // has already typed.
  const valueInVentaForFactura =
    contieneFactura
      ? needsTC
        ? Number.isFinite(pesosNum) && pesosNum > 0
          ? pesosNum
          : undefined
        : Number.isFinite(montoNum) && montoNum > 0
          ? montoNum
          : undefined
      : undefined;
  const facturaCap = round2(disponibleAFacturar);
  const excedido =
    valueInVentaForFactura != null &&
    valueInVentaForFactura > facturaCap + 0.005;

  // Espejo sin factura: un pago sin factura no puede crecer más allá del cupo
  // sin factura de la venta (en 0 —venta al 100%— no admite ningún monto).
  const valueInVentaSinFactura =
    !contieneFactura
      ? needsTC
        ? Number.isFinite(pesosNum) && pesosNum > 0
          ? pesosNum
          : undefined
        : Number.isFinite(montoNum) && montoNum > 0
          ? montoNum
          : undefined
      : undefined;
  const sinFacturaCap = round2(disponibleSinFactura);
  const excedidoSinFactura =
    valueInVentaSinFactura != null &&
    valueInVentaSinFactura > sinFacturaCap + 0.005;

  // Fiscal gate: this pago lleva factura but the client's fiscal data is
  // incomplete → block saving (Facturación couldn't emit its CAE). Here
  // `contieneFactura` is forced by the medio policy / existing flag (not a
  // toggle), so the vendedor must complete the client's condición IVA / CUIT
  // before saving any edit — which is the point: only they can.
  const fiscalBlocked = contieneFactura && (clienteFiscalMissing?.length ?? 0) > 0;

  const canSubmit =
    (!medioRequired || medio !== null) &&
    Number.isFinite(montoNum) &&
    montoNum > 0 &&
    Boolean(fechaCobro) &&
    (!hasPesos || (Number.isFinite(tcNum) && tcNum > 0 && Number.isFinite(pesosNum))) &&
    !excedido &&
    !excedidoSinFactura &&
    !fiscalBlocked;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (medioRequired && !medio) return;
    onSubmit({
      medioPagoId: medio?.id ?? '',
      medioPago:
        medio?.fields.Medio_MDP?.trim() ||
        (f.MedioPago_CP ?? '').trim() ||
        '',
      fechaCobro: isoToAr(fechaCobro),
      monto: montoNum,
      tipoCambio: hasPesos ? tcNum : undefined,
      pesos: hasPesos ? pesosNum : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !loading && onCancel()}>
      <DialogContent
        hideClose={loading}
        className="max-w-xl"
        processing={loading && 'Guardando cambios…'}
      >
        <DialogHeader>
          <DialogTitle>Editar Pago</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1">
            <Label className="text-sm">Fecha a cobrar</Label>
            <DatePicker
              value={fechaCobro}
              onChange={setFechaCobro}
              ariaLabel="Fecha a cobrar"
              clearable={false}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-sm">Método</Label>
              {mode === 'confirmacion' ? (
                <div className="flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm text-muted-foreground">
                  {medio?.fields.Medio_MDP?.trim() || f.MedioPago_CP || '—'}
                </div>
              ) : (
                <Combobox
                  value={medio}
                  onChange={setMedio}
                  items={mediosDePago}
                  getKey={(m) => m.id}
                  getLabel={(m) => m.fields.Medio_MDP?.trim() || '—'}
                  getSublabel={(m) => (m.fields.Categoria_MDP === 'Internacional' ? 'USD' : homeCurrency)}
                  matcher={(m, q) =>
                    (m.fields.Medio_MDP ?? '').toLowerCase().includes(q) ||
                    (m.fields.Categoria_MDP === 'Internacional' ? 'usd' : homeCurrency.toLowerCase()).includes(q)
                  }
                  placeholder="Seleccionar método"
                />
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Monto</Label>
              <MoneyInput
                variant="filled"
                value={monto}
                moneda={
                  medio?.fields.Categoria_MDP === 'Internacional' ? 'USD' : homeCurrency
                }
                onChange={setMonto}
                disabled={loading}
                className="h-10 text-right"
              />
            </div>
          </div>
          {hasPesos && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm">Tipo de cambio</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  variant="filled"
                  value={tipoCambio}
                  onChange={(e) => setTipoCambio(sanitizeDecimal(e.target.value))}
                  disabled={loading}
                  className="h-10 text-right"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Equivalente</Label>
                <MoneyInput
                  variant="filled"
                  value={pesos}
                  moneda="ARS"
                  onChange={setPesos}
                  disabled={loading}
                  className="h-10 text-right"
                />
              </div>
            </div>
          )}
          {excedido && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <p className="font-medium">
                {isExento ? 'Monto excedido' : 'Monto excedido para facturar'}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed">
                {isExento
                  ? `El monto supera el total de la venta (${formatMoney(facturaCap, ventaMoneda)}). Reducí el monto.`
                  : medioFuerzaFactura
                    ? `Este método de pago debe llevar factura de manera obligatoria y el monto supera lo disponible (${formatMoney(facturaCap, ventaMoneda)}). Reducí el monto antes de continuar.`
                    : `El monto supera lo disponible para facturar (${formatMoney(facturaCap, ventaMoneda)}). Reducí el monto antes de continuar.`}
              </p>
            </div>
          )}
          {excedidoSinFactura && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <p className="font-medium">Monto excedido sin factura</p>
              <p className="mt-0.5 text-xs leading-relaxed">
                {sinFacturaCap > 0
                  ? `Este pago va sin factura y el monto supera lo que la venta admite cobrar sin factura (${formatMoney(sinFacturaCap, ventaMoneda)}). Reducí el monto antes de continuar.`
                  : 'Este pago va sin factura y la venta no admite cobros sin factura. Anulalo y registralo de nuevo con Lleva Factura.'}
              </p>
            </div>
          )}
          {contieneFactura && <FiscalDataMissingWarning missing={clienteFiscalMissing} />}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function sanitizeDecimal(value: string): string {
  const cleaned = value.replace(/[^\d.,-]/g, '');
  const match = cleaned.match(/^-?\d*([.,]\d*)?/);
  return match ? match[0] : '';
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isoToAr(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function arToIso(ar: string | null | undefined): string {
  if (!ar) return '';
  const [d, m, y] = ar.split('/');
  if (!d || !m || !y) return '';
  return `${y}-${m}-${d}`;
}
